import { useTranslation } from 'react-i18next';
import { EntityPicker } from '@/shared/ui/components/composed/entity-picker/EntityPicker';
import { backendClientsCrud } from '@services/housien';

type ClientRecord = Record<string, unknown>;

interface ClientPickerProps {
  agencyId: string;
  onSelect: (client: ClientRecord) => void;
  onClose: () => void;
}

/**
 * Fetches an agency's clients for the picker, unwrapped from the paginated
 * CRUD response.
 *
 * @param agencyId - The agency identifier.
 * @returns The client records.
 */
async function fetchClients(agencyId: string): Promise<ClientRecord[]> {
  const result = await backendClientsCrud(agencyId).search({ pagination: { page: 1, limit: 50 } });
  return Object.values(result.data || {}) as ClientRecord[];
}

/**
 * Modal picker that lists an agency's clients (with search) and lets the user
 * select one as the partner-search reference.
 *
 * Thin wrapper around the shared `EntityPicker` — mirrors the property picker.
 *
 * @param props - The agency id, selection callback, and close callback.
 * @returns The client selection modal.
 */
export function ClientPicker({ agencyId, onSelect, onClose }: ClientPickerProps) {
  const { t } = useTranslation('features/partnerships');

  return (
    <EntityPicker<ClientRecord>
      agencyId={agencyId}
      onSelect={onSelect}
      onClose={onClose}
      title={t('wizard.pick_client')}
      fetchItems={fetchClients}
      getKey={(c) => c.client_id as string}
      matchesSearch={(c, search) => {
        const label = `${c.full_name || ''} ${c.email || ''}`.toLowerCase();
        return label.includes(search.toLowerCase());
      }}
      renderPrimary={(c) => (c.full_name as string) || 'Client'}
      renderSecondary={(c) => (
        <>
          {(c.email as string) || ''} {(c.phone as string) ? `• ${c.phone}` : ''}
        </>
      )}
    />
  );
}
